/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikErrors } from 'formik';

import {
  currencyUnformat,
  formatNumber,
  numberFormatWithDecimals,
} from '../helpers/utilityFunctions';
import useGetAvailableBalance from './useGetAvailableBalance';
import useGetCommissionsAvailableBalance from './useGetCommissionsAvailableBalance';

interface HandleAmountChangeParams {
  e: React.ChangeEvent<HTMLInputElement>;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<void> | Promise<FormikErrors<any>>;
  fieldValue: string;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  checkForAvailableBalance?: boolean;
  checkForCommissionsBalance?: boolean;
  currency?: string;
  minimumAmount?: number;
  maximumAmount?: number;
  shouldCheckAvailableBalanceInDifferentCurrency?: boolean;
  currencyRateToCompare?: number;
}

export default function useHandleAmountChange() {
  const availableBalanceInNaira = useGetAvailableBalance();

  const availableCommissionInNaira = useGetCommissionsAvailableBalance();

  const businessId = localStorage.getItem('businessId') ?? '';

  const shouldExemptFromLimit = process.env.REACT_APP_LIMIT_BUSINESS;
  const shouldExemptFromLimitArray = shouldExemptFromLimit?.split(',');

  return ({
    e,
    setFieldValue,
    fieldValue,
    setErrorMessage,
    checkForAvailableBalance,
    checkForCommissionsBalance,
    shouldCheckAvailableBalanceInDifferentCurrency,
    currency = '₦',
    minimumAmount = 100,
    maximumAmount = 250000000,
    currencyRateToCompare = 1,
  }: HandleAmountChangeParams) => {
    const inputValue = e.target.value;

    if (inputValue.endsWith('..') || /^(.*?\..*?){2}.*$/.test(inputValue)) {
      return;
    }

    const unformattedValue = currencyUnformat(inputValue);

    let differentCurrencyToCheckAvailableBalance;

    if (shouldCheckAvailableBalanceInDifferentCurrency) {
      differentCurrencyToCheckAvailableBalance =
        unformattedValue * currencyRateToCompare;
    }

    if (unformattedValue < minimumAmount) {
      setErrorMessage(
        `Amount cannot be lesser than ${currency}${minimumAmount ?? '100'}`
      );
    } else if (
      checkForAvailableBalance &&
      (availableBalanceInNaira === null ||
        (differentCurrencyToCheckAvailableBalance ?? unformattedValue) >
          Number(availableBalanceInNaira))
    ) {
      setErrorMessage('Insufficient balance');
    } else if (
      checkForCommissionsBalance &&
      (availableCommissionInNaira === null ||
        (differentCurrencyToCheckAvailableBalance ?? unformattedValue) >
          Number(availableCommissionInNaira))
    ) {
      setErrorMessage('Insufficient balance');
    } else if (
      unformattedValue > maximumAmount &&
      !shouldExemptFromLimitArray?.includes(businessId)
    ) {
      setErrorMessage(
        `Amount cannot be greater than ${currency}${
          formatNumber(maximumAmount).formattedString ?? '250,000,000'
        }`
      );
    } else {
      setErrorMessage('');
    }

    if (unformattedValue === 0) {
      setFieldValue(fieldValue, '');
      setErrorMessage('');

      return;
    }

    let formattedValue = numberFormatWithDecimals(unformattedValue);

    if (inputValue.endsWith('.')) {
      formattedValue = `${numberFormatWithDecimals(unformattedValue)}.`;
    }

    setFieldValue(fieldValue, formattedValue);
  };
}
